/**
 * igcomOrdersStore — the shapes and the pure rules behind IgcomOrdersService.
 *
 * Split out from the service so the two promises the store makes can be tested
 * without a SharePoint client: that no contact detail ever reaches the list,
 * and that a re-sync of an unchanged window writes nothing. Importing the
 * service itself pulls in spClient, which pulls in the MSAL config, which
 * refuses to load without the app's env vars — a test of a JSON packer has no
 * business needing a tenant id.
 *
 * Every rule about WHAT may be stored lives here; the service only moves rows.
 */

/**
 * On the CRM site, whose lists are plainly named — B2B_Orders, CRM_Customers,
 * EmbroideryRequests. NOT an `iSyn2_` name and NOT the Manufacturing site: IG
 * COM is a CRM screen, its list belongs beside the CRM lists, and the
 * Manufacturing site's Migration Valve does not provision it.
 */
export const IGCOM_ORDERS_LIST = "IGCOM_Orders";

/**
 * How many orders ride in one row.
 *
 * A packed order is ~180 characters once the SKUs are on it, so 120 of them is
 * a little over 20KB of JSON — the same ballpark ORDER_STATUS's 150 lands in,
 * comfortably inside a multi-line text column. Chunking is invisible to the
 * reader: the fold rebuilds the index from every row's entries however they
 * were grouped.
 */
export const ORDERS_PER_ROW = 120;

/** An order's stored facts. No contact details — see the header. */
export interface IIgcomStoredOrder {
  /** Shopify order id (GID), the identity the fold dedupes on. */
  id: string;
  /** The human order number, e.g. "#1042". */
  name: string;
  /** ISO timestamp the order was placed. */
  createdAt: string;
  financialStatus: string;
  fulfillmentStatus: string;
  total: number;
  currency: string;
  /** Pseudonymous — a Shopify customer GID, or `h:<hash>` for a guest. */
  customerKey: string;
  /** The name on the order. Already shown unmasked to every IG COM viewer, so
   *  storing it discloses nothing the screen does not. */
  customerName: string;
  city: string;
  province: string;
  /** Total units across the order's lines. */
  quantity: number;
  /** The SKUs on the order, deduped — what a trend is actually about. */
  skus: string[];
}

/** The shape a caller hands in: the live API row, contact fields and all. This
 *  service drops them; the caller does not have to remember to. */
export interface IIgcomSyncInput {
  id: string;
  name: string;
  createdAt: string;
  financialStatus: string;
  fulfillmentStatus: string;
  total: number;
  currency: string;
  customerKey: string;
  customerName: string;
  city: string;
  province: string;
  lineItems: readonly { sku: string; quantity: number }[];
}

export interface IIgcomStore {
  /** Order id -> its latest stored facts. */
  orders: IIgcomStoredOrder[];
  /** When the newest chunk was written, ISO, or '' where nothing is stored. */
  syncedAt: string;
  /** Who wrote it. */
  syncedBy: string;
  /** True when the list has not been provisioned yet — not an error, a state
   *  the screen explains and offers to fix. */
  missing: boolean;
}

/**
 * One order inside a chunk. Short keys: this is written thousands of times and
 * the column is not infinite.
 */
interface IPacked {
  i: string; n: string; d: string; f?: string; u?: string;
  t: number; c?: string; k: string; m?: string;
  y?: string; p?: string; q: number; s?: string[];
}

/** The currency every order on this store is in; written only when an order
 *  differs from it, which none have so far. */
const DEFAULT_CURRENCY = "PHP";

/**
 * A stable, readable-address-free key for a guest checkout.
 *
 * FNV-1a, not a cryptographic hash, and the comment above says why that is the
 * right call: the job is to keep an email address out of a SharePoint column,
 * not to defeat an attacker who already has the list and a customer list to
 * match it against.
 */
export function hashCustomerKey(raw: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < raw.length; i++) {
    h ^= raw.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `h:${h.toString(16).padStart(8, "0")}`;
}

/** Anything that reads like an address becomes a hash; a Shopify GID does not. */
export function safeCustomerKey(key: string): string {
  const k = (key || "").trim();
  if (!k) return "";
  if (k.startsWith("gid://")) return k;
  return hashCustomerKey(k.toLowerCase());
}

/** Drop the contact fields and flatten the lines. The ONE place an API row
 *  becomes a stored row, so there is one place to check that it is safe. */
export function toStoredOrder(o: IIgcomSyncInput): IIgcomStoredOrder {
  const skus = [...new Set((o.lineItems ?? []).map((l) => (l.sku || "").trim()).filter(Boolean))];
  return {
    id: o.id,
    name: o.name,
    createdAt: o.createdAt,
    financialStatus: o.financialStatus || "",
    fulfillmentStatus: o.fulfillmentStatus || "",
    total: Number(o.total) || 0,
    currency: o.currency || DEFAULT_CURRENCY,
    customerKey: safeCustomerKey(o.customerKey),
    customerName: o.customerName || "",
    city: o.city || "",
    province: o.province || "",
    quantity: (o.lineItems ?? []).reduce((n, l) => n + (Number(l.quantity) || 0), 0),
    skus,
  };
}

/** Everything about an order that a sync could have changed. Compared as a
 *  string so "the same order again" is one cheap test rather than nine. */
function fingerprint(o: IIgcomStoredOrder): string {
  return [
    o.name, o.createdAt, o.financialStatus, o.fulfillmentStatus,
    o.total, o.currency, o.customerKey, o.customerName, o.city, o.province,
    o.quantity, o.skus.join("|"),
  ].join("\u0001");
}

/**
 * Which of `live` the list does not already agree with.
 *
 * Exported and pure so the "a re-sync of an unchanged window writes nothing"
 * promise is testable without a SharePoint client.
 */
export function changedOrders(
  live: readonly IIgcomStoredOrder[],
  stored: readonly IIgcomStoredOrder[],
): IIgcomStoredOrder[] {
  const have = new Map(stored.map((o) => [o.id, fingerprint(o)]));
  return live.filter((o) => have.get(o.id) !== fingerprint(o));
}

export function pack(o: IIgcomStoredOrder): IPacked {
  return {
    i: o.id, n: o.name, d: o.createdAt, q: o.quantity, t: o.total, k: o.customerKey,
    ...(o.financialStatus ? { f: o.financialStatus } : {}),
    ...(o.fulfillmentStatus ? { u: o.fulfillmentStatus } : {}),
    ...(o.currency && o.currency !== DEFAULT_CURRENCY ? { c: o.currency } : {}),
    ...(o.customerName ? { m: o.customerName } : {}),
    ...(o.city ? { y: o.city } : {}),
    ...(o.province ? { p: o.province } : {}),
    ...(o.skus.length ? { s: o.skus } : {}),
  };
}

/**
 * A chunk's entries.
 *
 * A row whose JSON will not parse contributes NOTHING rather than throwing:
 * one malformed row from a hand-edit should cost its own orders, not every
 * other chunk on the list. Same posture as OrderStatusService.unpack.
 */
export function unpackChunk(raw: unknown): IIgcomStoredOrder[] {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const v: unknown = JSON.parse(raw);
    if (!Array.isArray(v)) return [];
    return v.flatMap((e): IIgcomStoredOrder[] => {
      const p = e as Partial<IPacked>;
      if (!p.i) return [];
      return [{
        id: String(p.i),
        name: String(p.n ?? ""),
        createdAt: String(p.d ?? ""),
        financialStatus: String(p.f ?? ""),
        fulfillmentStatus: String(p.u ?? ""),
        total: Number(p.t) || 0,
        currency: String(p.c ?? DEFAULT_CURRENCY),
        customerKey: String(p.k ?? ""),
        customerName: String(p.m ?? ""),
        city: String(p.y ?? ""),
        province: String(p.p ?? ""),
        quantity: Number(p.q) || 0,
        skus: Array.isArray(p.s) ? p.s.map(String) : [],
      }];
    });
  } catch {
    return [];
  }
}

